import { z } from 'zod';

/**
 * Restoring a platform backup onto a CLEAN install.
 *
 * The counterpart of `backup.ts`: those types describe taking a copy, these
 * describe putting one back on a machine that has nothing. Three things have
 * to be true before a single byte of the live database is touched, and the
 * shapes below exist so the UI can show each of them separately rather than
 * a single pass/fail:
 *
 *  1. WHO — a token only someone with root on the new box can read.
 *  2. WHAT — sha256 against the manifest, then `PRAGMA integrity_check`.
 *  3. THE KEY — the typed `APP_SECRET_KEY` actually decrypts a ciphertext
 *     taken out of the backup itself. A wrong key fails the AES-GCM tag.
 *
 * Nothing here ever carries the key back to the caller. It goes in, it is
 * tested, it is written to `.env`, and it is never echoed, logged or stored
 * anywhere else.
 */

// --- storage credentials, typed into the form ---------------------------------

/**
 * A clean install has no storage settings — they live inside the backup it is
 * trying to fetch. So the form supplies them directly for the one purpose of
 * listing and downloading a set; they are used for that request and thrown
 * away. The real settings arrive with the restored database.
 */
export const restoreStorageInputSchema = z.object({
  driver: z.enum(['local', 's3', 'bunny']),
  local: z.object({ dir: z.string().min(1).max(400) }).optional(),
  s3: z
    .object({
      endpoint: z.string().url(),
      region: z.string().min(1).max(64).default('us-east-1'),
      bucket: z.string().min(1).max(200),
      accessKeyId: z.string().min(1).max(200),
      secretAccessKey: z.string().min(1).max(400),
      forcePathStyle: z.boolean().default(true),
    })
    .optional(),
  bunny: z
    .object({
      host: z.string().min(1).max(200).default('storage.bunnycdn.com'),
      zone: z.string().min(1).max(200),
      accessKey: z.string().min(1).max(400),
    })
    .optional(),
});
export type RestoreStorageInput = z.infer<typeof restoreStorageInputSchema>;

/** One backup set found in the store, described from its own manifest. */
export const remoteBackupSetSchema = z.object({
  /** Storage prefix the set lives under, e.g. `platform-backups/2026-08-28-daily`. */
  prefix: z.string(),
  backupId: z.string(),
  /** Null when the set has no readable manifest — it can still be fetched. */
  takenAt: z.number().int().nullable(),
  version: z.string().nullable(),
  dbBytes: z.number().int().nullable(),
  sha256: z.string().nullable(),
  kind: z.string().nullable(),
  /**
   * daily | weekly | monthly, from the manifest. Null on manual sets and on
   * sets mirrored before tiers existed (their folders are row ids) — read,
   * never guessed from the name.
   */
  tier: z.string().nullable(),
  /** Why a set could not be described, when that happened. */
  problem: z.string().nullable(),
});
export type RemoteBackupSet = z.infer<typeof remoteBackupSetSchema>;

// --- what the box says about itself -------------------------------------------

/**
 * Why a restore is or is not possible here. Counts are only ever returned for
 * an install that IS empty — on a populated box this endpoint says "no" and
 * nothing else, because it answers before anyone has proved anything.
 */
export const restoreReadinessSchema = z.object({
  /** Empty install, an access anchor is configured, and the database is a local file. */
  ready: z.boolean(),
  empty: z.boolean(),
  /** A restore token hash exists on this server for the form to check against. */
  anchorConfigured: z.boolean(),
  /** Human sentences naming every reason a restore is refused right now. */
  blockers: z.array(z.string()),
  /** Rows found, only when the install is empty (all of them are 0 or 1 then). */
  counts: z.record(z.number().int()).nullable(),
  /** Largest upload this API will take, in bytes. */
  maxUploadBytes: z.number().int(),
});
export type RestoreReadiness = z.infer<typeof restoreReadinessSchema>;

// --- a candidate sitting in the staging area ----------------------------------

export const stagedBackupSchema = z.object({
  stagingId: z.string(),
  source: z.enum(['upload', 'remote']),
  /** Where it came from, in words — a filename or a storage prefix. */
  sourceLabel: z.string(),
  bytes: z.number().int(),
  sha256: z.string(),
  receivedAt: z.number().int(),

  /** The manifest, when one was supplied or found beside the snapshot. */
  manifest: z
    .object({
      backupId: z.string().nullable(),
      simpleacs: z.string().nullable(),
      takenAt: z.number().int().nullable(),
      kind: z.string().nullable(),
      dbBytes: z.number().int().nullable(),
      sha256: z.string().nullable(),
      envMode: z.string().nullable(),
      includesFiles: z.boolean().nullable(),
    })
    .nullable(),
  /**
   * Whether the snapshot's sha256 equals the one the manifest recorded.
   * Null means no manifest was supplied — the check could not be run, which
   * is reported rather than treated as a pass.
   */
  manifestMatch: z.boolean().nullable(),

  integrity: z.object({ ok: z.boolean(), detail: z.string() }),
  /** What is inside the snapshot, so the operator recognises their own data. */
  contents: z
    .object({
      organizations: z.number().int(),
      users: z.number().int(),
      olts: z.number().int(),
      onus: z.number().int(),
      devices: z.number().int(),
      sites: z.number().int(),
    })
    .nullable(),

  /** True when every automatic check passed and only the key is outstanding. */
  ready: z.boolean(),
  problems: z.array(z.string()),
  warnings: z.array(z.string()),
});
export type StagedBackup = z.infer<typeof stagedBackupSchema>;

// --- the key, and the result of using it --------------------------------------

/**
 * `proved`      — a ciphertext out of the backup decrypted with the typed key.
 * `unprovable`  — the backup holds no encrypted value at all, so there is
 *                 nothing to test the key against. See the note on
 *                 `acceptUnverifiableKey`.
 */
export const keyVerdictSchema = z.enum(['proved', 'unprovable']);
export type KeyVerdict = z.infer<typeof keyVerdictSchema>;

export const applyRestoreInputSchema = z.object({
  stagingId: z.string().min(1).max(80),
  /**
   * The old server's `APP_SECRET_KEY`. Deliberately typed rather than shipped
   * in the backup: the database in the bucket is encrypted with it, and a
   * bucket holding both is one compromise away from plaintext.
   */
  appSecretKey: z.string().min(1).max(1000),
  /**
   * Only meaningful when the backup contains no encrypted row at all. Then the
   * key cannot be proved right or wrong, and going ahead is an explicit
   * decision rather than a silent pass.
   */
  acceptUnverifiableKey: z.boolean().optional(),
});
export type ApplyRestoreInput = z.infer<typeof applyRestoreInputSchema>;

export const restoreResultSchema = z.object({
  ok: z.literal(true),
  restoredFrom: z.string(),
  dbBytes: z.number().int(),
  keyVerdict: keyVerdictSchema,
  /** Which ciphertext proved the key — table and column, never a value. */
  keyProvedBy: z.string().nullable(),
  /** The `.env` the key was written into, and at what mode. Never the key. */
  envPath: z.string().nullable(),
  envWritten: z.boolean(),
  /**
   * Where the pre-restore database was set aside, in case it is wanted.
   *
   * NULL on Postgres, and that is not an omission. The SQLite restore swapped a
   * file, so it copied the old one aside first and could hand back a path. The
   * Postgres restore drops a database — which it is only allowed to do because
   * restore is refused on any install that is not empty, so what it drops holds
   * nothing but a seed. There is no copy because there was nothing worth
   * copying, and returning a path to a file that does not exist would be worse
   * than saying so.
   */
  rollbackCopy: z.string().nullable(),
  restart: z.enum(['scheduled', 'manual']),
  /** Everything still left for a person to do, in order. */
  nextSteps: z.array(z.string()),
});
export type RestoreResult = z.infer<typeof restoreResultSchema>;
